import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, CalendarClock, HeartPulse, Landmark, Pencil, Plus, Scale, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { EmptyState } from '../components/ui/EmptyState';
import { Select } from '../components/ui/Select';
import { Switch } from '../components/ui/Switch';
import { api } from '../lib/api';
import { useBranches } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

// PT/LWF match a staff member's branch by exact string equality on "state" — so the state
// picked here MUST be one of the strings already set on a branch, never re-typed. A free-text
// field on both sides was the actual bug: "Gujarat" on the branch vs "gujarat" here would
// silently match nothing and the person's PT/LWF would just come out ₹0 with no error anywhere.
function useBranchStates() {
  const branches = useBranches();
  const states = [...new Set((branches.data ?? []).map((b) => b.state).filter((s): s is string => !!s))].sort();
  return { states, isLoading: branches.isLoading };
}

type PfSettings = { employeeRatePercent: string; employerRatePercent: string; wageCeiling: string; applyWageCeiling: boolean; isActive: boolean };
type EsiSettings = { employeeRatePercent: string; employerRatePercent: string; wageCeiling: string; isActive: boolean };
type PolicySettings = { weekOffWorkMultiplier: string; isActive: boolean };
type PtSlab = { id: string; state: string; minAmount: string; maxAmount: string | null; taxAmount: string; isActive: boolean };
type LwfSetting = { id: string; state: string; employeeAmount: string; employerAmount: string; isActive: boolean };

// Every rate/amount here defaults to 0/inactive server-side — nothing is deducted or paid
// until HR enters a real number. "All rates and slabs are stored as settings, not fixed in
// the software" (SoW §6.9) — a government rate change is an edit here, never a deploy.
function PfTab() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const query = useQuery({ queryKey: ['payroll-settings', 'pf'], queryFn: async () => (await api.get<PfSettings>('/api/payroll-settings/pf')).data });
  const [employeeRatePercent, setEmployeeRatePercent] = useState('');
  const [employerRatePercent, setEmployerRatePercent] = useState('');
  const [wageCeiling, setWageCeiling] = useState('');
  const [applyWageCeiling, setApplyWageCeiling] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [editing, setEditing] = useState(false);

  function startEdit() {
    const d = query.data;
    setEmployeeRatePercent(d?.employeeRatePercent ?? '0'); setEmployerRatePercent(d?.employerRatePercent ?? '0');
    setWageCeiling(d?.wageCeiling ?? '0'); setApplyWageCeiling(d?.applyWageCeiling ?? false); setIsActive(d?.isActive ?? false);
    setEditing(true);
  }
  const save = useMutation({
    mutationFn: () => api.patch('/api/payroll-settings/pf', { employeeRatePercent, employerRatePercent, wageCeiling, applyWageCeiling, isActive }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['payroll-settings', 'pf'] }); success('PF settings updated'); setEditing(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not save PF settings')),
  });

  if (query.isLoading) return <Loader />;
  const d = query.data!;

  if (editing) {
    return (
      <form className="card grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
        <label className="label">Employee rate (%)<input type="number" step="0.01" min="0" max="100" className="field mt-1" value={employeeRatePercent} onChange={(e) => setEmployeeRatePercent(e.target.value)} /></label>
        <label className="label">Employer rate (%)<input type="number" step="0.01" min="0" max="100" className="field mt-1" value={employerRatePercent} onChange={(e) => setEmployerRatePercent(e.target.value)} /></label>
        <label className="label">Wage ceiling (₹)<input type="number" step="0.01" min="0" className="field mt-1" value={wageCeiling} onChange={(e) => setWageCeiling(e.target.value)} /></label>
        <label className="label flex items-center gap-3">Apply wage ceiling<Switch checked={applyWageCeiling} onChange={setApplyWageCeiling} /></label>
        <label className="label flex items-center gap-3 sm:col-span-2">Active (deduct PF in payroll runs)<Switch checked={isActive} onChange={setIsActive} /></label>
        <div className="flex items-end gap-2 sm:col-span-2">
          <button className="btn-primary" disabled={save.isPending}>Save</button>
          <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </form>
    );
  }
  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-bold"><Landmark size={18} /> Provident Fund</h3>
          <p className="mt-2 text-sm text-muted">{d.isActive ? `${d.employeeRatePercent}% employee / ${d.employerRatePercent}% employer` : 'Not active — no PF deducted'}</p>
          {d.applyWageCeiling && <p className="mt-1 text-sm text-muted">Wage ceiling ₹{d.wageCeiling}</p>}
        </div>
        <button className="btn-secondary !px-3 shrink-0" aria-label="Edit PF settings" onClick={startEdit}><Pencil size={16} /></button>
      </div>
    </div>
  );
}

function EsiTab() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const query = useQuery({ queryKey: ['payroll-settings', 'esi'], queryFn: async () => (await api.get<EsiSettings>('/api/payroll-settings/esi')).data });
  const [employeeRatePercent, setEmployeeRatePercent] = useState('');
  const [employerRatePercent, setEmployerRatePercent] = useState('');
  const [wageCeiling, setWageCeiling] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [editing, setEditing] = useState(false);

  function startEdit() {
    const d = query.data;
    setEmployeeRatePercent(d?.employeeRatePercent ?? '0'); setEmployerRatePercent(d?.employerRatePercent ?? '0');
    setWageCeiling(d?.wageCeiling ?? '0'); setIsActive(d?.isActive ?? false);
    setEditing(true);
  }
  const save = useMutation({
    mutationFn: () => api.patch('/api/payroll-settings/esi', { employeeRatePercent, employerRatePercent, wageCeiling, isActive }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['payroll-settings', 'esi'] }); success('ESI settings updated'); setEditing(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not save ESI settings')),
  });

  if (query.isLoading) return <Loader />;
  const d = query.data!;

  if (editing) {
    return (
      <form className="card grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
        <label className="label">Employee rate (%)<input type="number" step="0.01" min="0" max="100" className="field mt-1" value={employeeRatePercent} onChange={(e) => setEmployeeRatePercent(e.target.value)} /></label>
        <label className="label">Employer rate (%)<input type="number" step="0.01" min="0" max="100" className="field mt-1" value={employerRatePercent} onChange={(e) => setEmployerRatePercent(e.target.value)} /></label>
        <label className="label">Wage ceiling (₹)<input type="number" step="0.01" min="0" className="field mt-1" value={wageCeiling} onChange={(e) => setWageCeiling(e.target.value)} /><span className="mt-1 block text-xs text-muted">Staff earning above this in a month are not ESI-eligible that period.</span></label>
        <label className="label flex items-center gap-3">Active (deduct ESI in payroll runs)<Switch checked={isActive} onChange={setIsActive} /></label>
        <div className="flex items-end gap-2 sm:col-span-2">
          <button className="btn-primary" disabled={save.isPending}>Save</button>
          <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </form>
    );
  }
  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-bold"><HeartPulse size={18} /> ESI</h3>
          <p className="mt-2 text-sm text-muted">{d.isActive ? `${d.employeeRatePercent}% employee / ${d.employerRatePercent}% employer` : 'Not active — no ESI deducted'}</p>
          <p className="mt-1 text-sm text-muted">Wage ceiling ₹{d.wageCeiling}</p>
        </div>
        <button className="btn-secondary !px-3 shrink-0" aria-label="Edit ESI settings" onClick={startEdit}><Pencil size={16} /></button>
      </div>
    </div>
  );
}

function PolicyTab() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const query = useQuery({ queryKey: ['payroll-settings', 'policy'], queryFn: async () => (await api.get<PolicySettings>('/api/payroll-settings/policy')).data });
  const [weekOffWorkMultiplier, setWeekOffWorkMultiplier] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [editing, setEditing] = useState(false);

  function startEdit() {
    setWeekOffWorkMultiplier(query.data?.weekOffWorkMultiplier ?? '0'); setIsActive(query.data?.isActive ?? false);
    setEditing(true);
  }
  const save = useMutation({
    mutationFn: () => api.patch('/api/payroll-settings/policy', { weekOffWorkMultiplier, isActive }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['payroll-settings', 'policy'] }); success('Policy updated'); setEditing(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not save policy')),
  });

  if (query.isLoading) return <Loader />;
  const d = query.data!;

  if (editing) {
    return (
      <form className="card grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
        <label className="label">Week-off work multiplier<input type="number" step="0.25" min="0" className="field mt-1" value={weekOffWorkMultiplier} onChange={(e) => setWeekOffWorkMultiplier(e.target.value)} /><span className="mt-1 block text-xs text-muted">1.00 = one extra day's wage for working a designated week-off day (e.g. Sunday). 2.00 = double.</span></label>
        <label className="label flex items-center gap-3">Active<Switch checked={isActive} onChange={setIsActive} /></label>
        <div className="flex items-end gap-2 sm:col-span-2">
          <button className="btn-primary" disabled={save.isPending}>Save</button>
          <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </form>
    );
  }
  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-bold"><Scale size={18} /> Week-off work bonus</h3>
          <p className="mt-2 text-sm text-muted">{d.isActive ? `${d.weekOffWorkMultiplier}x extra-day pay for working a designated week-off day` : 'Not active — no bonus for working a week-off day'}</p>
        </div>
        <button className="btn-secondary !px-3 shrink-0" aria-label="Edit policy" onClick={startEdit}><Pencil size={16} /></button>
      </div>
    </div>
  );
}

function PtSlabsTab() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const { states, isLoading: statesLoading } = useBranchStates();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const slabs = useQuery({ queryKey: ['pt-slabs'], queryFn: async () => (await api.get<PtSlab[]>('/api/pt-slabs')).data });

  const create = useMutation({
    mutationFn: () => api.post('/api/pt-slabs', { state, minAmount, maxAmount: maxAmount || null, taxAmount }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['pt-slabs'] }); success('PT slab added'); setOpen(false); setMinAmount(''); setMaxAmount(''); setTaxAmount(''); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not add slab')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/pt-slabs/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['pt-slabs'] }); success('Slab removed'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not remove slab')),
  });

  const sorted = [...(slabs.data ?? [])].sort((a, b) => a.state.localeCompare(b.state) || Number(a.minAmount) - Number(b.minAmount));

  return (
    <>
      <div className="mb-4 flex items-center justify-between"><p className="text-muted">Professional Tax slabs, per state and income range — matched by the branch each staff member works at.</p><button className="btn-primary" onClick={() => setOpen(!open)}><Plus size={18} /> New</button></div>
      {open && states.length === 0 && !statesLoading && (
        <p className="card mb-4 text-sm text-muted">No branch has a state configured yet. Set a state on at least one branch (Settings &rarr; Branches) before adding a PT slab — slabs are matched to staff by their branch's state.</p>
      )}
      {open && states.length > 0 && (
        <form className="card mb-4 grid gap-4 sm:grid-cols-4" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
          <label className="label">State<Select className="mt-1" required value={state} onChange={(e) => setState(e.target.value)}><option value="">Select…</option>{states.map((st) => <option key={st} value={st}>{st}</option>)}</Select></label>
          <label className="label">Min income (₹)<input type="number" min="0" className="field mt-1" required value={minAmount} onChange={(e) => setMinAmount(e.target.value)} /></label>
          <label className="label">Max income (₹, blank = no limit)<input type="number" min="0" className="field mt-1" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} /></label>
          <label className="label">PT amount (₹)<input type="number" min="0" className="field mt-1" required value={taxAmount} onChange={(e) => setTaxAmount(e.target.value)} /></label>
          <div className="flex items-end gap-2 sm:col-span-4"><button className="btn-primary" disabled={create.isPending}>Save</button><button type="button" className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button></div>
        </form>
      )}
      {!slabs.isLoading && sorted.length === 0 && <EmptyState title="No PT slabs configured yet" />}
      <div className="space-y-2">
        {sorted.map((s) => (
          <div key={s.id} className="card flex items-center justify-between gap-3 py-3">
            <div className="text-sm"><strong>{s.state}</strong> <span className="text-muted">₹{s.minAmount} – {s.maxAmount ?? '∞'}</span></div>
            <div className="flex items-center gap-3"><strong>₹{s.taxAmount}</strong><button className="text-muted hover:text-red-700" aria-label="Remove slab" onClick={() => { if (confirm('Remove this PT slab?')) remove.mutate(s.id); }}><Trash2 size={15} /></button></div>
          </div>
        ))}
      </div>
    </>
  );
}

function LwfTab() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const { states, isLoading: statesLoading } = useBranchStates();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('');
  const [employeeAmount, setEmployeeAmount] = useState('');
  const [employerAmount, setEmployerAmount] = useState('');
  const settings = useQuery({ queryKey: ['lwf-settings'], queryFn: async () => (await api.get<LwfSetting[]>('/api/lwf-settings')).data });

  const create = useMutation({
    mutationFn: () => api.post('/api/lwf-settings', { state, employeeAmount, employerAmount }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['lwf-settings'] }); success('LWF setting added'); setOpen(false); setEmployeeAmount(''); setEmployerAmount(''); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not add setting')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/lwf-settings/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['lwf-settings'] }); success('Setting removed'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not remove setting')),
  });

  const sorted = [...(settings.data ?? [])].sort((a, b) => a.state.localeCompare(b.state));

  return (
    <>
      <div className="mb-4 flex items-center justify-between"><p className="text-muted">Labour Welfare Fund — flat amount per state.</p><button className="btn-primary" onClick={() => setOpen(!open)}><Plus size={18} /> New</button></div>
      {open && states.length === 0 && !statesLoading && (
        <p className="card mb-4 text-sm text-muted">No branch has a state configured yet. Set a state on at least one branch (Settings &rarr; Branches) before adding an LWF setting — settings are matched to staff by their branch's state.</p>
      )}
      {open && states.length > 0 && (
        <form className="card mb-4 grid gap-4 sm:grid-cols-3" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
          <label className="label">State<Select className="mt-1" required value={state} onChange={(e) => setState(e.target.value)}><option value="">Select…</option>{states.map((st) => <option key={st} value={st}>{st}</option>)}</Select></label>
          <label className="label">Employee amount (₹)<input type="number" min="0" className="field mt-1" required value={employeeAmount} onChange={(e) => setEmployeeAmount(e.target.value)} /></label>
          <label className="label">Employer amount (₹)<input type="number" min="0" className="field mt-1" required value={employerAmount} onChange={(e) => setEmployerAmount(e.target.value)} /></label>
          <div className="flex items-end gap-2 sm:col-span-3"><button className="btn-primary" disabled={create.isPending}>Save</button><button type="button" className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button></div>
        </form>
      )}
      {!settings.isLoading && sorted.length === 0 && <EmptyState title="No LWF settings configured yet" />}
      <div className="space-y-2">
        {sorted.map((s) => (
          <div key={s.id} className="card flex items-center justify-between gap-3 py-3">
            <strong className="text-sm">{s.state}</strong>
            <div className="flex items-center gap-3 text-sm"><span>₹{s.employeeAmount} employee / ₹{s.employerAmount} employer</span><button className="text-muted hover:text-red-700" aria-label="Remove setting" onClick={() => { if (confirm('Remove this LWF setting?')) remove.mutate(s.id); }}><Trash2 size={15} /></button></div>
          </div>
        ))}
      </div>
    </>
  );
}

export function PayrollSettingsPage() {
  const [tab, setTab] = useState<'pf' | 'esi' | 'pt' | 'lwf' | 'policy'>('pf');
  const tabs = [['pf', 'PF', Landmark], ['esi', 'ESI', HeartPulse], ['pt', 'PT Slabs', Banknote], ['lwf', 'LWF', Scale], ['policy', 'Policy', CalendarClock]] as const;
  return (
    <>
      <div className="mb-6"><h1 className="text-3xl font-bold">Payroll settings</h1><p className="mt-1 text-muted">Statutory rates and slabs — all configurable, never hardcoded.</p></div>
      <div className="mb-6 flex flex-wrap gap-2 border-b border-border">
        {tabs.map(([key, label, Icon]) => <button key={key} className={`flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold ${tab === key ? 'border-brand-600 text-brand-700' : 'border-transparent text-muted'}`} onClick={() => setTab(key)}><Icon size={16} /> {label}</button>)}
      </div>
      {tab === 'pf' && <PfTab />}
      {tab === 'esi' && <EsiTab />}
      {tab === 'pt' && <PtSlabsTab />}
      {tab === 'lwf' && <LwfTab />}
      {tab === 'policy' && <PolicyTab />}
    </>
  );
}
